import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getChargingConnector, updateChargingConnector, getConnectorStandards } from "../../api/ChargingConnector";
import { getAllChargingPoints } from "../../api/ChargingPoint";
import { getChargingSite, getChargingSiteParkingSpaces } from "../../api/ChargingSites";
import { getSimpleTariffsByOperatorOrganizationPublicId } from "../../api/Tariffs";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import ConfirmationModal from "../../components/ConfirmationModal";

import EvseSettingsForm from "../../components/point/EvseSettingsForm";
import ConnectorActions from "../../components/connector/ConnectorActions";
import ConnectorForm from "../../components/connector/ConnectorForm";
import { useTranslation } from "react-i18next";

const EditChargingConnectorsPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const { id } = useParams();
    const location = useLocation();
    const { chargingStationPublicId, chargingPointPublicId, chargingSitePublicId, operatorOrganizationPublicId } =
        location.state || {};
    const navigate = useNavigate();
    const [resolvedOperatorOrganizationPublicId, setResolvedOperatorOrganizationPublicId] =
        useState(operatorOrganizationPublicId ?? null);

    const [chargingPoints, setChargingPoints] = useState([]);
    const [chargingConnector, setChargingConnector] = useState(null);
    const [originalChargingConnector, setOriginalChargingConnector] = useState(null);
    const [standards, setStandards] = useState([]);
    const [isMultiStation, setIsMultiStation] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [loadingConnector, setLoadingConnector] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const [confirmState, setConfirmState] = useState({
        isOpen: false,
        title: "",
        description: "",
        okText: undefined,
        onConfirm: null,
    });
    const query = new URLSearchParams(location.search);
    const [activeTab, setActiveTab] = useState(query.get("tab") || "details");
    const [tariffs, setTariffs] = useState([]);
    const [parkingSpaces, setParkingSpaces] = useState([]);

    // Sync tab with URL
    useEffect(() => {
        const tab = query.get("tab");
        if (tab) setActiveTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [location.search]);

    useEffect(() => {
        const q = new URLSearchParams(location.search);
        q.set("tab", activeTab);
        navigate(`${location.pathname}?${q.toString()}`, {
            replace: true,
            state: {
                chargingStationPublicId,
                chargingPointPublicId:
                    chargingPointPublicId ?? chargingConnector?.chargingPointPublicId,
                chargingSitePublicId,
                operatorOrganizationPublicId: resolvedOperatorOrganizationPublicId,
            },
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [activeTab]);

    // Determine multi-station
    useEffect(() => {
        if (location.state?.isMultiStationChargingPoint != null) {
            setIsMultiStation(!!location.state.isMultiStationChargingPoint);
        }
    }, [location.state]);

    useEffect(() => {
        if (!chargingConnector || chargingPoints.length === 0) return;
        const parentPoint = chargingPoints.find(p => p.chargingPointPublicId === chargingConnector.chargingPointPublicId);
        setIsMultiStation(!!parentPoint?.isMultiStationChargingPoint);
    }, [chargingConnector, chargingPoints]);

    // Track changes
    useEffect(() => {
        if (!originalChargingConnector) return;
        const connectorChanged = JSON.stringify(chargingConnector) !== JSON.stringify(originalChargingConnector);
        setHasChanges(connectorChanged);
    }, [chargingConnector, originalChargingConnector]);

    // Fetch charging points
    useEffect(() => {
        const loadAll = async () => {
            try {
                const token = await getAccessToken();

                const [points, connectorData, standardsData] = await Promise.all([
                    getAllChargingPoints(token),
                    id !== "0" ? getChargingConnector(id, token) : Promise.resolve(null),
                    getConnectorStandards(token),
                ]);

                let operatorOrgPublicId = operatorOrganizationPublicId ?? null;
                if (!operatorOrgPublicId) {
                    const sitePublicId =
                        chargingSitePublicId ||
                        (connectorData?.chargingPointPublicId
                            ? points.find(
                                  (point) =>
                                      point.chargingPointPublicId ===
                                      connectorData.chargingPointPublicId
                              )?.chargingSitePublicId
                            : null);

                    if (sitePublicId) {
                        const site = await getChargingSite(sitePublicId, token);
                        operatorOrgPublicId = site?.operator?.organizationPublicId ?? null;
                    }
                }

                setResolvedOperatorOrganizationPublicId(operatorOrgPublicId);

                let tariffsData = [];
                if (operatorOrgPublicId) {
                    tariffsData = await getSimpleTariffsByOperatorOrganizationPublicId(
                        operatorOrgPublicId,
                        token
                    );
                }

                setChargingPoints(points);
                setStandards(standardsData || []);
                setTariffs(tariffsData || []);

                if (connectorData) {
                    const con = {
                        ...connectorData,
                        maxElectricPower: connectorData.maxElectricPower ?? connectorData.maxKw ?? "",
                        maxAmperage: connectorData.maxAmperage ?? "",
                        maxVoltage: connectorData.maxVoltage ?? "",
                        powerType: connectorData.powerType ?? "",
                        capabilities: connectorData.capabilities ?? [],
                        directions: connectorData.directions ?? [],
                        parkingRestrictions: connectorData.parkingRestrictions ?? [],
                        evseCapabilities: connectorData.evseCapabilities ?? connectorData.capabilities ?? [],
                        statusSchedules: connectorData.statusSchedules ?? [],
                        parkings: connectorData.parkings ?? [],
                        floorLevel: connectorData.floorLevel ?? "",
                        physicalReference: connectorData.physicalReference ?? "",
                        latitude: connectorData.latitude ?? null,
                        longitude: connectorData.longitude ?? null,
                        acceptedServiceProviders: connectorData.acceptedServiceProviders ?? [],
                        tariffIds: connectorData.tariffIds ?? [],
                        reservationEnabled: connectorData.reservationEnabled ?? false,
                    };

                    setChargingConnector(con);
                    setOriginalChargingConnector(con);
                } else {
                    setChargingConnector({});
                    setOriginalChargingConnector({});
                }

            } catch (err) {
                toast.error(t("toast.failedLoad", { entity: t("common.details") }));
            } finally {
                setLoadingConnector(false);   
            }
        };

        loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [id]);

    useEffect(() => {
        const loadParkingSpaces = async () => {
            if (!chargingConnector?.chargingPointPublicId || chargingPoints.length === 0) {
                return;
            }

            const parentPoint = chargingPoints.find(
                (point) => point.chargingPointPublicId === chargingConnector.chargingPointPublicId
            );

            const sitePublicId = parentPoint?.chargingSitePublicId;
            if (!sitePublicId) {
                setParkingSpaces([]);
                return;
            }

            try {
                const token = await getAccessToken();
                const spaces = await getChargingSiteParkingSpaces(sitePublicId, token);
                setParkingSpaces(spaces || []);
            } catch (error) {
                toast.error(error?.message || t("toast.failedFetch", { entity: t("entity.parking") }));
                setParkingSpaces([]);
            }
        };

        loadParkingSpaces();
    }, [chargingConnector?.chargingPointPublicId, chargingPoints, getAccessToken]);


    const closeConfirmModal = () => {
        setConfirmState({
            isOpen: false,
            title: "",
            description: "",
            okText: undefined,
            onConfirm: null,
        });
    };

    const handleConfirmModalConfirm = () => {
        const fn = confirmState.onConfirm;
        closeConfirmModal();
        if (typeof fn === "function") {
            fn();
        }
    };

    const handleInputChange = (key, value) => {
        if (
            key === "reservationEnabled" &&
            value === false &&
            chargingConnector?.reservationEnabled === true
        ) {
            setConfirmState({
                isOpen: true,
                title: t("confirm.turnOffReservations"),
                description:
                    t("confirm.turnOffReservationsDescription"),
                okText: t("confirm.turnOffReservationsOk"),
                onConfirm: () => {
                    setHasChanges(true);
                    setChargingConnector((prev) => ({ ...prev, reservationEnabled: false }));
                },
            });
            return;
        }

        setHasChanges(true);
        setChargingConnector((prev) => ({ ...prev, [key]: value }));
    };


    const navigateBack = () => {
        const returnPointId =
            chargingPointPublicId ?? chargingConnector?.chargingPointPublicId;
        if (chargingSitePublicId) {
            navigate(`/site/${chargingSitePublicId}`);
        } else if (returnPointId) {
            navigate(`/point/${returnPointId}?tab=connectors`);
        } else if (chargingStationPublicId) {
            navigate(`/station/${chargingStationPublicId}`);
        } else {
            navigate("/connectors");
        }
    };

    const handleBackClick = () => {
        if (hasChanges) {
            setConfirmState({
                isOpen: true,
                title: t("confirm.discard"),
                description: "",
                okText: undefined,
                onConfirm: () => {
                    navigateBack();
                },
            });
        } else {
            navigateBack();
        }
    };

    const saveAndExit = () => {
        if (chargingConnector?.chargingConnectorPublicId) {
            // Update existing
            handleUpdateChargingConnector();
        } else {
            navigate("/connectors");
        }
    };

    const validateConnectorBeforeSave = (connector) => {
        const missing = [];

        // Required references
        if (!connector?.chargingPointPublicId) missing.push("chargingPointPublicId");

        // Name
        if (!connector?.chargingConnectorName?.toString().trim())
            missing.push("chargingConnectorName");

        // Connector number
        if (
            connector?.chargingConnectorNumber == null ||
            connector?.chargingConnectorNumber === "" ||
            Number(connector.chargingConnectorNumber) <= 0
        ) {
            missing.push("chargingConnectorNumber");
        }

        // Standard & format
        if (!connector?.standard) missing.push("standard");
        if (!connector?.format) missing.push("format");

        // Power fields
        if (connector.maxElectricPower == null || Number(connector.maxElectricPower) <= 0)
            missing.push("maxElectricPower");
        if (connector.maxAmperage == null || Number(connector.maxAmperage) <= 0)
            missing.push("maxAmperage");
        if (connector.maxVoltage == null || Number(connector.maxVoltage) <= 0)
            missing.push("maxVoltage");
        if (!connector?.powerType) missing.push("powerType");

        if (!connector?.tariffIds?.length) missing.push("tariffIds");

        return missing; 

    };


    const handleUpdateChargingConnector = async () => {
        if (!chargingConnector) {
            toast.error(t("common.nothingToSave"));
            return;
        }

        // Validering, t.ex. maxElectricPower, maxAmperage etc.
        const missing = validateConnectorBeforeSave(chargingConnector);
        if (missing.length > 0) {
            setInvalidFields(missing);
            toast.error(t("common.pleaseFillRequiredFields", { fields: missing.join(", ") }));
            return;
        }
        setInvalidFields([]);

        const toastId = toast.loading(t("toast.updating", { entity: t("entity.connector") }));

        try {
            const token = await getAccessToken();

            const payload = {
                ...chargingConnector,
                maxElectricPower: chargingConnector.maxElectricPower != null ? Number(chargingConnector.maxElectricPower) : null,
                maxAmperage: chargingConnector.maxAmperage != null ? Number(chargingConnector.maxAmperage) : null,
                maxVoltage: chargingConnector.maxVoltage != null ? Number(chargingConnector.maxVoltage) : null,
                tariffIds: chargingConnector.tariffIds ?? [],
            };

            const updated = await updateChargingConnector(payload, token);

            const next = updated ? { ...updated } : { ...chargingConnector, ...payload };
            setChargingConnector(next);
            setOriginalChargingConnector(next);
            setHasChanges(false);

            toast.success(t("toast.updated", { entity: t("entity.connector") }), { id: toastId });
        } catch (error) {
            console.error(error);
            toast.error(error?.message || t("toast.failedUpdate", { entity: t("entity.connector") }), { id: toastId });
        }
    };

    if (loadingConnector) {
        return (
            <>
                <HeaderBar />
                <div className="flex flex-row w-full">
                    <Sidebar />

                    <main className="page flex items-center justify-center">
                        <LoadingSpinner text={t("common.loadingEntity", { name: t("form.connectorDetails") })} />
                    </main>
                </div>
            </>
        );
    }


    return (

        <>

            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />

                <main className="page">
                    <div className={`${loadingConnector ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={handleBackClick} className="text-black flex ">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4">
                                {id !== "0" ? chargingConnector?.chargingConnectorName : t("pages.createConnector")}
                            </h2>
                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={handleBackClick}>{t("common.cancel")}</button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!chargingConnector?.chargingConnectorName || !hasChanges}
                                    onClick={saveAndExit}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="border-b border-mute-200 mb-4 gap-4 flex">
                                <button
                                    className={`px-4 pb-2 ${activeTab === "details" ? "border-b-2 border-black font-bold text-sm text-black" : "text-mute-400 text-sm font-bold"}`}
                                    onClick={() => setActiveTab("details")}
                                >{t("tabs.details")}</button>
                                {isMultiStation && (
                                    <button
                                        className={`px-4 pb-2 ${activeTab === "evseSettings" ? "border-b-2 border-black font-bold text-sm text-black" : "text-mute-400 text-sm font-bold"}`}
                                        onClick={() => setActiveTab("evseSettings")}
                                    >{t("tabs.evseSettings")}</button>
                                )}
                            </div>

                            {activeTab === "details" && chargingConnector && (
                                <ConnectorForm
                                    chargingConnector={chargingConnector}
                                    handleInputChange={handleInputChange}
                                    chargingPoints={chargingPoints}
                                    invalidFields={invalidFields}
                                    isEdit={chargingConnector.chargingConnectorPublicId !== "0"}
                                    standards={standards}
                                    tariffs={tariffs}
                                />
                            )}

                            {activeTab === "actions" && chargingConnector && <ConnectorActions />}
                            {activeTab === "evseSettings" && chargingConnector && (
                                <EvseSettingsForm
                                    chargingConnector={chargingConnector}
                                    handleInputChange={handleInputChange}
                                    invalidFields={invalidFields}
                                    parkingSpaces={parkingSpaces}
                                />
                            )}
                        </div>
                    </div>
                </main>

                <ConfirmationModal
                    isOpen={confirmState.isOpen}
                    title={confirmState.title}
                    description={confirmState.description}
                    okText={confirmState.okText}
                    onClose={closeConfirmModal}
                    onConfirm={handleConfirmModalConfirm}
                />
            </div>
        </>
    );
};

export default EditChargingConnectorsPage;
